/**
 * Pure derivation logic for the Benchmarks tab's "Forecast skill" block (lane
 * P2 SCORE). Reads only `index.json.refs.benchmarks.forecast_skill`
 * (`organism.benchmark.forecast_skill.ForecastScorecard.report`); nothing here
 * computes a statistic the publisher did not — it only labels, orders and
 * formats, and degrades every missing value to "–" (never a guessed 0).
 */

import type {
  ForecastSkillDocument,
  ForecastSkillHorizon,
  ForecastSkillWindow,
} from "../types/incremental";
import { formatCount, formatPercent, formatSignedNumber, type ToneText } from "../format/number";

/** The forecaster acceptance band for central-90 coverage (GPU-TRAINING-PLAN §0). */
export const COVERAGE_90_BAND: readonly [number, number] = [0.88, 0.94];
/** Central-50 coverage within this distance of 0.50 reads as calibrated. */
export const COVERAGE_50_TOLERANCE = 0.05;

export type CalibrationFlag = "ok" | "off" | "na";

export interface ForecastSkillRow {
  readonly horizon: string;
  readonly horizonLabel: string;
  readonly insufficient: boolean;
  readonly pairs: string;
  readonly rankXs: ToneText;
  readonly rankXsPositive: string;
  readonly rankPooled: ToneText;
  readonly pearsonPooled: ToneText;
  readonly coverage50: string;
  readonly coverage50Flag: CalibrationFlag;
  readonly coverage90: string;
  readonly coverage90Flag: CalibrationFlag;
  readonly pinballSkill: ToneText;
}

export interface ForecastSkillWindowRows {
  readonly name: string;
  readonly range: string;
  readonly rows: readonly ForecastSkillRow[];
}

export interface ForecastSkillSymbolRow {
  readonly symbol: string;
  readonly pairs: string;
  /** Pooled Pearson IC per horizon, in `horizons` order. */
  readonly ics: readonly ToneText[];
  /** Central-90 coverage at the longest horizon. */
  readonly coverage90: string;
}

export type ForecastSkillState =
  | { readonly kind: "absent" }
  | {
      readonly kind: "pending";
      readonly title: string;
      readonly message: string;
      readonly footer: string;
    }
  | {
      readonly kind: "ready";
      readonly status: ForecastSkillDocument["status"];
      readonly message: string | null;
      readonly horizons: readonly string[];
      readonly horizonLabels: readonly string[];
      readonly windows: readonly ForecastSkillWindowRows[];
      readonly symbolWindow: string | null;
      readonly symbols: readonly ForecastSkillSymbolRow[];
      readonly footer: string;
    };

/** "5 bars" at 60 s bars -> "5 min"; any other bar length keeps "bars". */
export function horizonLabel(horizon: string | number, barSeconds: number): string {
  const bars = Number(horizon);
  if (!Number.isFinite(bars)) return String(horizon);
  if (barSeconds === 60) return bars >= 60 && bars % 60 === 0 ? `${bars / 60} h` : `${bars} min`;
  return `${bars} bars`;
}

export function formatIc(value: number | null | undefined): ToneText {
  return formatSignedNumber(value ?? null, 3);
}

export function coverageFlag(value: number | null | undefined, central: 50 | 90): CalibrationFlag {
  if (value === null || value === undefined || !Number.isFinite(value)) return "na";
  if (central === 90) {
    return value >= COVERAGE_90_BAND[0] && value <= COVERAGE_90_BAND[1] ? "ok" : "off";
  }
  return Math.abs(value - 0.5) <= COVERAGE_50_TOLERANCE ? "ok" : "off";
}

function row(
  horizon: string,
  metrics: ForecastSkillHorizon | undefined,
  barSeconds: number,
): ForecastSkillRow {
  return {
    horizon,
    horizonLabel: horizonLabel(horizon, barSeconds),
    insufficient: !metrics || metrics.status !== "ok",
    pairs: formatCount(metrics?.n ?? null),
    rankXs: formatIc(metrics?.ic_rank_xs),
    rankXsPositive: formatPercent(metrics?.ic_rank_xs_positive ?? null, 0),
    rankPooled: formatIc(metrics?.ic_rank_pooled),
    pearsonPooled: formatIc(metrics?.ic_pearson_pooled),
    coverage50: formatPercent(metrics?.coverage_50 ?? null, 1),
    coverage50Flag: coverageFlag(metrics?.coverage_50, 50),
    coverage90: formatPercent(metrics?.coverage_90 ?? null, 1),
    coverage90Flag: coverageFlag(metrics?.coverage_90, 90),
    pinballSkill: formatSignedNumber(metrics?.pinball_skill ?? null, 3),
  };
}

function windowRows(
  name: string,
  window: ForecastSkillWindow,
  horizons: readonly string[],
  barSeconds: number,
): ForecastSkillWindowRows {
  return {
    name,
    range: `${window.from} → ${window.to}`,
    rows: horizons.map((h) => row(h, window.horizons[h], barSeconds)),
  };
}

/** Per-symbol rows from the LONGEST window that has any pairs. */
function symbolRows(
  windows: Readonly<Record<string, ForecastSkillWindow>>,
  horizons: readonly string[],
): { window: string | null; rows: ForecastSkillSymbolRow[] } {
  const names = Object.keys(windows).sort(
    (a, b) => Number.parseInt(b, 10) - Number.parseInt(a, 10),
  );
  for (const name of names) {
    const window = windows[name];
    if (!window) continue;
    const symbols = new Set<string>();
    for (const h of horizons) {
      for (const symbol of Object.keys(window.horizons[h]?.per_symbol ?? {})) symbols.add(symbol);
    }
    if (symbols.size === 0) continue;
    const longest = horizons[horizons.length - 1];
    const rows = [...symbols].sort().map((symbol) => {
      const first =
        horizons[0] !== undefined ? window.horizons[horizons[0]]?.per_symbol[symbol] : undefined;
      const last = longest !== undefined ? window.horizons[longest]?.per_symbol[symbol] : undefined;
      return {
        symbol,
        pairs: formatCount(first?.n ?? null),
        ics: horizons.map((h) =>
          formatIc(window.horizons[h]?.per_symbol[symbol]?.ic_pearson_pooled),
        ),
        coverage90: formatPercent(last?.coverage_90 ?? null, 0),
      };
    });
    return { window: name, rows };
  }
  return { window: null, rows: [] };
}

/** One-line provenance: counts, why forecasts were not scored, which forecaster. */
export function skillFooter(doc: ForecastSkillDocument): string {
  const counts = doc.counts ?? {};
  const seen = counts["wakes_seen"] ?? 0;
  const without = counts["wakes_without_forecast"] ?? 0;
  const parts = [
    `Since the scorecard started: ${formatCount(seen - without)} wakes with a forecast`,
    `${formatCount(counts["wakes_scored"] ?? 0)} scored`,
    `${formatCount(counts["pairs_scored"] ?? 0)} realized pairs`,
    `${formatCount(counts["pending_wakes"] ?? 0)} pending`,
  ];
  const dropped = Object.entries(doc.dropped ?? {})
    .filter(([, n]) => n > 0)
    .map(([reason, n]) => `${reason.replace(/_/g, " ")} ${formatCount(n)}`);
  if (dropped.length > 0) parts.push(`dropped: ${dropped.join(", ")}`);
  // The windows score `current_forecaster` only; fall back to the most recent digest.
  const digest = doc.current_forecaster ?? doc.forecasters?.[0]?.digest ?? null;
  if (digest) {
    const entry = doc.forecasters?.find((f) => f.digest === digest);
    const trained = entry?.trained === false ? " (UNTRAINED)" : "";
    parts.push(`forecaster ${digest.slice(0, 12)}${trained}`);
  }
  const previous = doc.previous_forecasters?.length ?? 0;
  if (previous > 0) {
    parts.push(`${previous} earlier forecaster${previous === 1 ? "" : "s"} scored separately`);
  }
  return parts.join(" · ");
}

export function buildForecastSkillState(
  doc: ForecastSkillDocument | null | undefined,
): ForecastSkillState {
  if (!doc) return { kind: "absent" };
  const footer = skillFooter(doc);
  if (doc.status === "no_forecasts") {
    return {
      kind: "pending",
      title: "No forecasts recorded yet",
      message:
        doc.message ??
        "No brain.wake in this catalog carries a forecast block yet (recorded from the P2 telemetry onward).",
      footer,
    };
  }
  const windows = doc.windows;
  const horizons = (doc.horizons_bars ?? []).map(String);
  if (!windows || horizons.length === 0) {
    return {
      kind: "pending",
      title: "Insufficient data",
      message: doc.message ?? "Forecasts are recorded but none has been scored yet.",
      footer,
    };
  }
  const names = Object.keys(windows).sort(
    (a, b) => Number.parseInt(a, 10) - Number.parseInt(b, 10),
  );
  const symbols = symbolRows(windows, horizons);
  return {
    kind: "ready",
    status: doc.status,
    message: doc.status === "ok" ? null : (doc.message ?? "Insufficient data."),
    horizons,
    horizonLabels: horizons.map((h) => horizonLabel(h, doc.bar_seconds)),
    windows: names.flatMap((name) => {
      const window = windows[name];
      return window ? [windowRows(name, window, horizons, doc.bar_seconds)] : [];
    }),
    symbolWindow: symbols.window,
    symbols: symbols.rows,
    footer,
  };
}

/** Column help text (header tooltips). */
export const SKILL_COLUMN_TIPS: Readonly<Record<string, string>> = {
  pairs:
    "Realized (forecast, outcome) pairs in the window: wakes × symbols whose horizon has elapsed.",
  rankXs:
    "Mean over wakes of the cross-sectional Spearman rank correlation between the forecast median and the realized standardized return (≥ 4 names). The share of wakes with a positive IC is in brackets.",
  rankPooled:
    "Spearman rank IC over all pairs, pooled per 1-hour block of forecast time (pair-weighted mean).",
  pearsonPooled:
    "Pearson IC of forecast median vs realized standardized return over every pair in the window.",
  coverage50:
    "Share of outcomes inside the forecast's central 50% band [q25, q75]. Calibrated ≈ 50%.",
  coverage90:
    "Share of outcomes inside the central 90% band [q05, q95]. Calibrated = 88–94% (the forecaster acceptance band).",
  pinballSkill:
    "1 − pinball loss / naive pinball loss. Naive = zero median with trailing-vol Gaussian quantiles. Above 0 = the forecaster beats it.",
};
